export type MerchantCreativePreviewStyle =
  | 'ambient'
  | 'framed-device'
  | 'product-focus';

export type MerchantCreativeFormat = {
  id: string;
  label: string;
  aspectRatio: '4:5' | '9:16' | '1:1';
  placementLabel: string;
  primaryText: string;
  headline: string;
  subheadline: string;
  footer: string;
  exportFileName: string;
};

export type MerchantLaunchConcept = {
  slug: string;
  angle: string;
  objective: string;
  audience: string;
  funnelStage: string;
  launchRecommendation: string;
  note: string;
  landingPath: string;
  paidDestinationUrl: string;
  cta: string;
  backgroundImage: string;
  accent: string;
  previewStyle?: MerchantCreativePreviewStyle;
  /**
   * Tailwind object-position class applied to the ambient background image
   * (e.g. `object-right`, `object-[70%_center]`). Only used when
   * `previewStyle` is `ambient` and the default `object-center` crop hides
   * the photo's focal subject in a tighter aspect ratio like 1:1.
   */
  ambientObjectPosition?: string;
  supportChips: string[];
  formats: MerchantCreativeFormat[];
};

export type MerchantLaunchOperatorCampaign = {
  campaignName: string;
  objective: string;
  conversionLocation: string;
  performanceGoal: string;
  dailyBudget: string;
  useCase: string;
  adSets: {
    adSetName: string;
    slug: MerchantLaunchConcept['slug'];
    audience: string;
    budget: string;
    metaCta: string;
  }[];
};

export type MerchantLaunchOperatorRow = {
  slug: MerchantLaunchConcept['slug'];
  angle: string;
  formatId: string;
  formatLabel: string;
  campaignName: string;
  adSetName: string;
  adName: string;
  objective: string;
  conversionLocation: string;
  performanceGoal: string;
  dailyBudget: string;
  metaCta: string;
  onCreativeFooter: string;
  audience: string;
  location: string;
  age: string;
  language: string;
  placements: string;
  assetFileName: string;
  assetWorkspacePath: string;
  assetPublicUrl: string;
  primaryText: string;
  headline: string;
  description: string;
  destinationUrl: string;
  landingPath: string;
};

const UTM_CAMPAIGN = 'merchant-acq-launch-2026-05-22';
const OPERATOR_LOCATION = 'Asheville, NC + 35 miles';
const OPERATOR_AGE = '25-64';
const OPERATOR_LANGUAGE = 'English';
const OPERATOR_CONVERSION_LOCATION = 'Website';
const OPERATOR_PERFORMANCE_GOAL = 'Maximize number of conversions';
const EXPORT_PUBLIC_ROOT = '/exports/facebook-merchant-launch';
const EXPORT_WORKSPACE_ROOT = 'websites/coupling-wnc/public/exports/facebook-merchant-launch';
const COLD_CAMPAIGN_NAME =
  'CWNC | Leads | Merchant Acquisition | Cold | 2026-05-22';
const WARM_CAMPAIGN_NAME =
  'CWNC | Leads | Merchant Acquisition | Warm-Narrow | 2026-05-22';

const buildPaidDestinationUrl = (path: string, content: string) =>
  `https://couplingwnc.com${path}?utm_source=facebook&utm_medium=paid&utm_campaign=${UTM_CAMPAIGN}&utm_content=${content}`;

export const merchantLaunchConcepts: MerchantLaunchConcept[] = [
  {
    slug: 'wnc-merchants',
    angle: 'Core merchant offer',
    objective: 'Cold merchant acquisition',
    audience: 'General WNC independent merchant traffic',
    funnelStage: 'Cold traffic',
    launchRecommendation:
      'Launch first. This is the broadest cold opener and the clearest merchant-first promise.',
    note: 'Best baseline creative when you want the ad, landing page, and demo CTA to stay tightly aligned.',
    landingPath: '/lp/wnc-merchants',
    paidDestinationUrl: buildPaidDestinationUrl(
      '/lp/wnc-merchants',
      'core-merchant-offer',
    ),
    cta: 'Learn More',
    backgroundImage: '/product/hero/coupling-hero-desktop-blended.jpg',
    accent: 'from-[#c95f33] via-[#a84b25] to-[#6a311d]',
    previewStyle: 'ambient',
    supportChips: ['Square, Clover, Toast', 'No monthly dues', 'Processor fees may apply'],
    formats: [
      {
        id: 'wnc-merchants-feed-4x5',
        label: 'Feed 4:5',
        aspectRatio: '4:5',
        placementLabel: 'Facebook / Instagram Feed',
        primaryText:
          'Rewards, vouchers, gift cards, and local offers for WNC merchants. Keep your current checkout flow, add a clearer loyalty offer, and see the whole merchant flow in a live demo.',
        headline: 'Turn every sale into a reason to come back.',
        subheadline: 'Rewards, vouchers, and gift cards for WNC merchants.',
        footer: 'Learn More',
        exportFileName: 'wnc-merchants-feed-4x5.png',
      },
      {
        id: 'wnc-merchants-story-9x16',
        label: 'Story 9:16',
        aspectRatio: '9:16',
        placementLabel: 'Facebook / Instagram Story',
        primaryText:
          'Rewards, vouchers, and gift cards for WNC merchants. Keep your checkout flow and see the merchant offer live.',
        headline: 'Turn every sale into a return visit.',
        subheadline: 'Keep your checkout. Add a better loyalty offer.',
        footer: 'Learn More',
        exportFileName: 'wnc-merchants-story-9x16.png',
      },
      {
        id: 'wnc-merchants-square-1x1',
        label: 'Square 1:1',
        aspectRatio: '1:1',
        placementLabel: 'Facebook / Instagram Feed + Marketplace',
        primaryText:
          'Rewards, vouchers, gift cards, and local offers for WNC merchants. Keep your current checkout flow, add a clearer loyalty offer, and see the whole merchant flow in a live demo.',
        headline: 'Turn every sale into a reason to come back.',
        subheadline: 'Rewards, vouchers, and gift cards for WNC merchants.',
        footer: 'Learn More',
        exportFileName: 'wnc-merchants-square-1x1.png',
      },
    ],
  },
  {
    slug: 'pos-mode',
    angle: 'POS compatibility',
    objective: 'Workflow-fit proof',
    audience: 'Merchants already using Square, Clover, or Toast',
    funnelStage: 'Cold or mid-funnel consideration',
    launchRecommendation:
      'Launch alongside the core merchant offer when workflow anxiety is the main objection.',
    note: 'This concept works best when the merchant already has hardware and needs proof that Coupling fits beside it.',
    landingPath: '/lp/pos-mode',
    paidDestinationUrl: buildPaidDestinationUrl('/lp/pos-mode', 'pos-mode'),
    cta: 'Learn More',
    backgroundImage: '/product/merchant-pos-terminal.webp',
    accent: 'from-[#40655f] via-[#264f4d] to-[#173736]',
    previewStyle: 'ambient',
    ambientObjectPosition: 'object-[68%_center]',
    supportChips: ['Keep your POS', 'Square, Clover, Toast', 'Staff-friendly rollout'],
    formats: [
      {
        id: 'pos-mode-feed-4x5',
        label: 'Feed 4:5',
        aspectRatio: '4:5',
        placementLabel: 'Facebook / Instagram Feed',
        primaryText:
          'Keep your POS. Add rewards, vouchers, and gift cards beside it. Coupling works with the systems your staff already uses, so the rollout is easier to picture and easier to test.',
        headline: 'Keep your checkout. Add better loyalty.',
        subheadline: 'Works with Square, Clover, and Toast.',
        footer: 'Learn More',
        exportFileName: 'pos-mode-feed-4x5.png',
      },
      {
        id: 'pos-mode-story-9x16',
        label: 'Story 9:16',
        aspectRatio: '9:16',
        placementLabel: 'Facebook / Instagram Story',
        primaryText:
          'Already using Square, Clover, or Toast? Keep it. Add rewards and gift cards beside the setup you already know.',
        headline: 'Keep your POS.',
        subheadline: 'Add rewards and gift cards beside it.',
        footer: 'Learn More',
        exportFileName: 'pos-mode-story-9x16.png',
      },
      {
        id: 'pos-mode-square-1x1',
        label: 'Square 1:1',
        aspectRatio: '1:1',
        placementLabel: 'Facebook / Instagram Feed + Marketplace',
        primaryText:
          'Keep your POS. Add rewards, vouchers, and gift cards beside it. Coupling works with the systems your staff already uses, so the rollout is easier to picture and easier to test.',
        headline: 'Keep your checkout. Add better loyalty.',
        subheadline: 'Works with Square, Clover, and Toast.',
        footer: 'Learn More',
        exportFileName: 'pos-mode-square-1x1.png',
      },
    ],
  },
  {
    slug: 'simple-pricing',
    angle: 'Simple pricing',
    objective: 'Price-led cold acquisition',
    audience: 'Cost-sensitive merchants comparing loyalty tools',
    funnelStage: 'Cold traffic',
    launchRecommendation:
      'Launch as a direct price/value ad for merchants who are comparing cost and complexity first.',
    note: 'Keep the message simple. Do not reintroduce token-payment language into this concept.',
    landingPath: '/lp/simple-pricing',
    paidDestinationUrl: buildPaidDestinationUrl(
      '/lp/simple-pricing',
      'simple-pricing',
    ),
    cta: 'Learn More',
    backgroundImage: '/product/simple-pricing-iphone-showcase.png',
    accent: 'from-[#f0bb4d] via-[#df9b34] to-[#b4691d]',
    previewStyle: 'framed-device',
    supportChips: ['Free core access', 'Optional offers may cost', 'Minting costs shown upfront'],
    formats: [
      {
        id: 'simple-pricing-feed-4x5',
        label: 'Feed 4:5',
        aspectRatio: '4:5',
        placementLabel: 'Facebook / Instagram Feed',
        primaryText:
          'Free merchant access with no monthly subscription dues. Token minting and ordinary card-processor costs may apply. Coupling gives WNC merchants a clearer way to run rewards, vouchers, and gift cards.',
        headline: 'No monthly subscription dues.',
        subheadline: 'Simple tools for WNC merchants.',
        footer: 'Learn More',
        exportFileName: 'simple-pricing-feed-4x5.png',
      },
      {
        id: 'simple-pricing-story-9x16',
        label: 'Story 9:16',
        aspectRatio: '9:16',
        placementLabel: 'Facebook / Instagram Story',
        primaryText:
          'Free merchant access with no monthly subscription dues. Token minting and ordinary card-processor costs may apply.',
        headline: 'Free merchant access.',
        subheadline: 'Minting costs shown upfront.',
        footer: 'Learn More',
        exportFileName: 'simple-pricing-story-9x16.png',
      },
      {
        id: 'simple-pricing-square-1x1',
        label: 'Square 1:1',
        aspectRatio: '1:1',
        placementLabel: 'Facebook / Instagram Feed + Marketplace',
        primaryText:
          'Free merchant access with no monthly subscription dues. Token minting and ordinary card-processor costs may apply. Coupling gives WNC merchants a clearer way to run rewards, vouchers, and gift cards.',
        headline: 'No monthly subscription dues.',
        subheadline: 'Simple tools for WNC merchants.',
        footer: 'Learn More',
        exportFileName: 'simple-pricing-square-1x1.png',
      },
    ],
  },
  {
    slug: 'local-merchant-network',
    angle: 'Local merchant network',
    objective: 'Differentiated community-growth angle',
    audience:
      'Local-economy minded merchants who are open to partnerships after product clarity',
    funnelStage: 'Narrower or warmer traffic',
    launchRecommendation:
      'Launch-ready, but best used for narrower or warmer traffic after the merchant offer is already understood.',
    note: 'Lead with merchant-controlled partnerships and capped acceptance, not abstract network language.',
    landingPath: '/lp/local-merchant-network',
    paidDestinationUrl: buildPaidDestinationUrl(
      '/lp/local-merchant-network',
      'local-merchant-network',
    ),
    cta: 'Learn More',
    backgroundImage: '/product/partnership-growth-ipad-tilted.png',
    accent: 'from-[#7f4d30] via-[#5f3a2a] to-[#2d2927]',
    previewStyle: 'product-focus',
    supportChips: [
      'Merchant-controlled partnerships',
      '$100 acceptance cap',
      'Keep value local',
    ],
    formats: [
      {
        id: 'local-merchant-network-feed-4x5',
        label: 'Feed 4:5',
        aspectRatio: '4:5',
        placementLabel: 'Facebook / Instagram Feed',
        primaryText:
          'Bring customers back, then grow into a stronger WNC merchant network. Coupling starts with a practical merchant offer and expands into controlled local connections when you are ready.',
        headline: 'Bring customers back. Keep value local.',
        subheadline: 'Grow into a WNC merchant network on your terms.',
        footer: 'See how it works',
        exportFileName: 'local-merchant-network-feed-4x5.png',
      },
      {
        id: 'local-merchant-network-story-9x16',
        label: 'Story 9:16',
        aspectRatio: '9:16',
        placementLabel: 'Facebook / Instagram Story',
        primaryText:
          'Grow into merchant-controlled local partnerships with clear acceptance limits.',
        headline: 'Keep value local.',
        subheadline: 'Grow partnerships on your terms.',
        footer: 'See how it works',
        exportFileName: 'local-merchant-network-story-9x16.png',
      },
      {
        id: 'local-merchant-network-square-1x1',
        label: 'Square 1:1',
        aspectRatio: '1:1',
        placementLabel: 'Facebook / Instagram Feed + Marketplace',
        primaryText:
          'Bring customers back, then grow into a stronger WNC merchant network. Coupling starts with a practical merchant offer and expands into controlled local connections when you are ready.',
        headline: 'Bring customers back. Keep value local.',
        subheadline: 'Grow into a WNC merchant network on your terms.',
        footer: 'See how it works',
        exportFileName: 'local-merchant-network-square-1x1.png',
      },
    ],
  },
];

export const getFeedCreative = (concept: MerchantLaunchConcept) =>
  concept.formats.find((format) => format.aspectRatio === '4:5') ?? concept.formats[0];

/**
 * PNG export sizing per aspect ratio.
 *
 * `cssWidth` is the CSS pixel width the creative card is laid out at (text and
 * spacing are tuned for this width). The `/preview/creative-export` route
 * applies a `transform: scale(pngWidth / cssWidth)` so the rendered creative
 * is upscaled uniformly to the target PNG size — keeping the exact look of the
 * preview, just at the resolution Meta recommends (≥1080px).
 */
export const CREATIVE_EXPORT_SIZING: Record<
  MerchantCreativeFormat['aspectRatio'],
  { cssWidth: number; pngWidth: number; pngHeight: number }
> = {
  '4:5': { cssWidth: 400, pngWidth: 1080, pngHeight: 1350 },
  '1:1': { cssWidth: 384, pngWidth: 1080, pngHeight: 1080 },
  '9:16': { cssWidth: 352, pngWidth: 1080, pngHeight: 1920 },
};

const operatorBlueprintBySlug: Record<
  MerchantLaunchConcept['slug'],
  {
    campaignName: string;
    adSetName: string;
    dailyBudget: string;
  }
> = {
  'wnc-merchants': {
    campaignName: COLD_CAMPAIGN_NAME,
    adSetName: 'CWNC | Core Merchant Offer | Broad WNC Merchants | 2026-05-22',
    dailyBudget: '$6.25/day',
  },
  'pos-mode': {
    campaignName: COLD_CAMPAIGN_NAME,
    adSetName: 'CWNC | POS Compatibility | POS Users | 2026-05-22',
    dailyBudget: '$6.25/day',
  },
  'simple-pricing': {
    campaignName: COLD_CAMPAIGN_NAME,
    adSetName: 'CWNC | Simple Pricing | Cost Sensitive Merchants | 2026-05-22',
    dailyBudget: '$6.25/day',
  },
  'local-merchant-network': {
    campaignName: WARM_CAMPAIGN_NAME,
    adSetName: 'CWNC | Local Merchant Network | Warm-Narrow | 2026-05-22',
    dailyBudget: '$6.25/day',
  },
};

const getOperatorPlacements = (format: MerchantCreativeFormat) => {
  switch (format.aspectRatio) {
    case '4:5':
      return 'Facebook Feed; Instagram Feed';
    case '1:1':
      return 'Facebook Feed; Instagram Feed; Facebook Marketplace; Facebook Search Results';
    case '9:16':
      return 'Facebook Stories; Instagram Stories; Facebook Reels; Instagram Reels';
  }
};

const getOperatorTrackingTerm = (format: MerchantCreativeFormat) => {
  switch (format.aspectRatio) {
    case '4:5':
      return 'feed-4x5';
    case '1:1':
      return 'square-1x1';
    case '9:16':
      return 'story-reels-9x16';
  }
};

const toOperatorLabel = (value: string) =>
  value.replace(/\b\w/g, (match) => match.toUpperCase());

const toOperatorFormatLabel = (value: string) => value.replace(':', 'x');

const buildOperatorDestinationUrl = (
  concept: MerchantLaunchConcept,
  format: MerchantCreativeFormat,
) => `${concept.paidDestinationUrl}&utm_term=${getOperatorTrackingTerm(format)}`;

export const merchantLaunchOperatorCampaigns: MerchantLaunchOperatorCampaign[] = [
  {
    campaignName: COLD_CAMPAIGN_NAME,
    objective: 'Leads',
    conversionLocation: OPERATOR_CONVERSION_LOCATION,
    performanceGoal: OPERATOR_PERFORMANCE_GOAL,
    dailyBudget: '$18.75/day total via ad set budgets',
    useCase:
      'Cold prospecting for the three clearest merchant-first concepts.',
    adSets: merchantLaunchConcepts
      .filter((concept) => concept.slug !== 'local-merchant-network')
      .map((concept) => ({
        adSetName: operatorBlueprintBySlug[concept.slug].adSetName,
        slug: concept.slug,
        audience: concept.audience,
        budget: operatorBlueprintBySlug[concept.slug].dailyBudget,
        metaCta: concept.cta,
      })),
  },
  {
    campaignName: WARM_CAMPAIGN_NAME,
    objective: 'Leads',
    conversionLocation: OPERATOR_CONVERSION_LOCATION,
    performanceGoal: OPERATOR_PERFORMANCE_GOAL,
    dailyBudget: '$6.25/day via ad set budget',
    useCase:
      'Warmer or narrower traffic for the local merchant network angle.',
    adSets: merchantLaunchConcepts
      .filter((concept) => concept.slug === 'local-merchant-network')
      .map((concept) => ({
        adSetName: operatorBlueprintBySlug[concept.slug].adSetName,
        slug: concept.slug,
        audience: concept.audience,
        budget: operatorBlueprintBySlug[concept.slug].dailyBudget,
        metaCta: concept.cta,
      })),
  },
];

export const merchantLaunchOperatorRows: MerchantLaunchOperatorRow[] =
  merchantLaunchConcepts.flatMap((concept) => {
    const blueprint = operatorBlueprintBySlug[concept.slug];

    return concept.formats.map((format) => ({
      slug: concept.slug,
      angle: concept.angle,
      formatId: format.id,
      formatLabel: format.label,
      campaignName: blueprint.campaignName,
      adSetName: blueprint.adSetName,
      adName: `CWNC | ${toOperatorLabel(concept.angle)} | ${toOperatorFormatLabel(format.label)} | ${concept.cta} | 2026-05-22`,
      objective: 'Leads',
      conversionLocation: OPERATOR_CONVERSION_LOCATION,
      performanceGoal: OPERATOR_PERFORMANCE_GOAL,
      dailyBudget: blueprint.dailyBudget,
      metaCta: concept.cta,
      onCreativeFooter: format.footer,
      audience: concept.audience,
      location: OPERATOR_LOCATION,
      age: OPERATOR_AGE,
      language: OPERATOR_LANGUAGE,
      placements: getOperatorPlacements(format),
      assetFileName: format.exportFileName,
      assetWorkspacePath: `${EXPORT_WORKSPACE_ROOT}/${format.exportFileName}`,
      assetPublicUrl: `${EXPORT_PUBLIC_ROOT}/${format.exportFileName}`,
      primaryText: format.primaryText,
      headline: format.headline,
      description: format.subheadline,
      destinationUrl: buildOperatorDestinationUrl(concept, format),
      landingPath: concept.landingPath,
    }));
  });

const operatorTsvHeaders = [
  'campaign_name',
  'ad_set_name',
  'ad_name',
  'objective',
  'conversion_location',
  'performance_goal',
  'daily_budget',
  'meta_cta',
  'on_creative_footer',
  'audience',
  'location',
  'age',
  'language',
  'placements',
  'asset_workspace_path',
  'asset_public_url',
  'primary_text',
  'headline',
  'description',
  'destination_url',
] as const;

const toTsvCell = (value: string) => value.replace(/\t/g, ' ').replace(/\n/g, ' ');

export const merchantLaunchOperatorTsv = [
  operatorTsvHeaders.join('\t'),
  ...merchantLaunchOperatorRows.map((row) =>
    [
      row.campaignName,
      row.adSetName,
      row.adName,
      row.objective,
      row.conversionLocation,
      row.performanceGoal,
      row.dailyBudget,
      row.metaCta,
      row.onCreativeFooter,
      row.audience,
      row.location,
      row.age,
      row.language,
      row.placements,
      row.assetWorkspacePath,
      row.assetPublicUrl,
      row.primaryText,
      row.headline,
      row.description,
      row.destinationUrl,
    ]
      .map(toTsvCell)
      .join('\t'),
  ),
].join('\n');
